import type {
  ExecutionOutputStream,
  ExecutionResult,
  PlaygroundRunOutcome,
} from "../types";

export type RunStatusTone = "success" | "warning" | "error" | "neutral";
export const CLIENT_EXECUTION_OUTPUT_LIMIT_BYTES = 100 * 1024;

/**
 * The runner's per-stream output cap, in the unit the badge names (`PG-OUT01`,
 * `docs/24` §5.4). The cap lives in `progsity-runner`; this is the number a
 * learner reads, so it changes only with that one.
 */
export const RUNNER_OUTPUT_LIMIT_MB = 1;
const utf8Encoder = new TextEncoder();

/**
 * The semantic class of a run, which every consumer branches on (`PG-UX03`).
 *
 * The seven values are the wire's `outcome` vocabulary; `unknown` is the eighth
 * and exists so a code this client has never seen renders as "unknown" with the
 * engine's own prose beside it, rather than as an error it is not or a success
 * it is not. Provider prose (`status`) is never a branch condition anywhere.
 */
export type RunStatusKind = PlaygroundRunOutcome | "unknown";

const KNOWN_OUTCOMES: ReadonlySet<string> = new Set<PlaygroundRunOutcome>([
  "success",
  "runtime_error",
  "compilation_error",
  "time_limit_exceeded",
  "memory_limit_exceeded",
  "output_limit_exceeded",
  "internal_error",
]);

/**
 * The kind for a result, from `outcome` and nothing else.
 *
 * Until 2026-09-06 a retired `statusId` number sat beside `outcome` and was the
 * fallback for a response that predated the field (expand). The web stopped
 * reading it first and the wire then dropped it (contract, ADR-0058, the
 * 2026-08-18 decision point 4); a cached response that still carries the number
 * renders exactly as one that does not. An `outcome` value this build does not
 * know is `unknown`, rendered with the engine's own prose beside it.
 */
export function kindFromResult(result: ExecutionResult): RunStatusKind {
  if (result.outcome && KNOWN_OUTCOMES.has(result.outcome)) return result.outcome;
  return "unknown";
}

/** next-intl keys under `playground`, one per kind; the UI never prints prose. */
export const RUN_STATUS_LABEL_KEY: Readonly<Record<RunStatusKind, string>> = Object.freeze({
  success: "statusSuccess",
  runtime_error: "statusRuntimeError",
  compilation_error: "statusCompileError",
  time_limit_exceeded: "statusTimeLimit",
  memory_limit_exceeded: "statusMemoryLimit",
  output_limit_exceeded: "statusOutputLimit",
  internal_error: "statusInternalError",
  unknown: "statusUnknown",
});

/**
 * The values a label may interpolate. Only `statusOutputLimit` reads one today;
 * next-intl ignores a value a message does not use, so every label gets them.
 */
export const RUN_STATUS_LABEL_VALUES = Object.freeze({ limitMb: RUNNER_OUTPUT_LIMIT_MB });

export function toneFromKind(kind: RunStatusKind): RunStatusTone {
  if (kind === "success") return "success";
  if (
    kind === "time_limit_exceeded" ||
    kind === "memory_limit_exceeded" ||
    kind === "output_limit_exceeded"
  ) {
    return "warning";
  }
  if (kind === "unknown") return "neutral";
  return "error";
}

/** The engine-neutral field, which is what new code should branch on. */
export function toneFromOutcome(outcome: PlaygroundRunOutcome): RunStatusTone {
  return toneFromKind(outcome);
}

/** The tone for a result, through {@link kindFromResult}. */
export function toneFromResult(result: ExecutionResult): RunStatusTone {
  return toneFromKind(kindFromResult(result));
}

/**
 * The run outcomes Fix My Error is offered for (`PG-AI02`, ADR-0051).
 *
 * A compile error and a runtime error come with a diagnostic a model can read.
 * The three limits do not: a program cut at its time limit says nothing about
 * why, and a memory or output limit says only that. `internal_error` and
 * `unknown` are not the student's code at all, which is what `statusRetryHint`
 * already tells them. Offering a paid answer for any of those five would be
 * selling a guess.
 */
export const FIX_ELIGIBLE_KINDS: ReadonlySet<RunStatusKind> = new Set<RunStatusKind>([
  "compilation_error",
  "runtime_error",
]);

export function isFixEligible(result: ExecutionResult | null): boolean {
  if (!result) return false;
  if (!FIX_ELIGIBLE_KINDS.has(kindFromResult(result))) return false;
  // A kind without a diagnostic is not something to ask about. The backend
  // refuses the same case; this only keeps a pointless request from starting.
  return Boolean(
    result.compileOutput?.trim() || result.stderr?.trim() || result.stdout?.trim(),
  );
}

export function hasCompileIssue(result: ExecutionResult | null): boolean {
  if (!result) return false;
  return result.outcome === "compilation_error" || Boolean(result.compileOutput?.trim());
}

function takeUtf8Prefix(value: string, maxBytes: number): {
  value: string;
  bytes: number;
  truncated: boolean;
} {
  const totalBytes = utf8Encoder.encode(value).byteLength;
  if (totalBytes <= maxBytes) {
    return { value, bytes: totalBytes, truncated: false };
  }
  if (maxBytes <= 0) return { value: "", bytes: 0, truncated: totalBytes > 0 };

  const chars: string[] = [];
  let bytes = 0;
  for (const char of value) {
    const charBytes = utf8Encoder.encode(char).byteLength;
    if (bytes + charBytes > maxBytes) break;
    chars.push(char);
    bytes += charBytes;
  }
  return { value: chars.join(""), bytes, truncated: true };
}

/**
 * Defense in depth for a stale/misconfigured backend: never hand an unbounded
 * provider payload to React. Diagnostics receive the shared byte budget first.
 */
export function guardExecutionResultForRendering(result: ExecutionResult): ExecutionResult {
  const outputs: Record<ExecutionOutputStream, string | null> = {
    stdout: result.stdout,
    stderr: result.stderr,
    compileOutput: result.compileOutput,
  };
  const truncatedStreams = new Set<ExecutionOutputStream>(result.truncatedStreams);
  let remainingBytes = CLIENT_EXECUTION_OUTPUT_LIMIT_BYTES;

  for (const stream of ["compileOutput", "stderr", "stdout"] as const) {
    const value = outputs[stream];
    if (value === null) continue;
    const chunk = takeUtf8Prefix(value, remainingBytes);
    outputs[stream] = chunk.value || null;
    remainingBytes -= chunk.bytes;
    if (chunk.truncated) truncatedStreams.add(stream);
  }

  return {
    ...result,
    ...outputs,
    outputTruncated: result.outputTruncated || truncatedStreams.size > 0,
    truncatedStreams: [...truncatedStreams],
  };
}
